import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ShieldCheck, Database, Trash2, Upload, Link2, BarChart3,
  Loader2, AlertTriangle, CheckCircle2, Download, FileText, Lock,
  Video, PlayCircle, X, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/library-v2`;

/**
 * Library Reset · Arquitectura limpia
 *  Flujo estricto:  1) Backup  →  2) Wipe (PIN)  →  3) Import CSV/JSON  →  4) Relink huérfanos  →  5) Stats
 *  Regla: 1 ejercicio = 1 exercise_id (GF-XXX) = 1 vídeo (GF-XXX.mp4)
 */
const LibraryResetPage = () => {
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState({});
  const [pin, setPin] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [wipeR2, setWipeR2] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const fileInputRef = useRef(null);
  // Paso 5: asociación de vídeos
  const [exercises, setExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEx, setSelectedEx] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const setBusy = (k, v) => setLoading((p) => ({ ...p, [k]: v }));

  const refreshStats = async () => {
    try {
      const r = await axios.get(`${API}/stats`);
      setStats(r.data);
    } catch (e) { /* noop */ }
  };

  const refreshBackups = async () => {
    try {
      const r = await axios.get(`${API}/backup/list`);
      setBackups(r.data.backups || []);
    } catch (e) { /* noop */ }
  };

  const refreshExercises = async () => {
    try {
      const r = await axios.get(`${API}/exercises`);
      setExercises(r.data.exercises || []);
      // Auto-select primero si nada seleccionado
      if ((r.data.exercises || []).length > 0 && !selectedEx) {
        setSelectedEx(r.data.exercises[0]);
      }
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    refreshStats();
    refreshBackups();
    refreshExercises();
  }, []);

  // Auto-refrescar ejercicios cuando se acaba de importar
  useEffect(() => {
    if (stats?.schema_v2 > 0 && exercises.length === 0) {
      refreshExercises();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats?.schema_v2]);

  const uploadVideo = async (exerciseId, file, opts = {}) => {
    if (!file) return;
    const force = opts.force || false;
    // Validación: nombre del archivo debe coincidir con exercise_id (recomendación)
    if (!force) {
      const baseName = file.name.replace(/\.[^/.]+$/, '').toUpperCase();
      if (baseName !== exerciseId.toUpperCase()) {
        const ok = window.confirm(
          `El archivo se llama "${file.name}" pero el exercise_id es "${exerciseId}". ¿Subirlo igualmente? (Recomendación: renombrar a ${exerciseId}.mp4)`
        );
        if (!ok) return;
      }
    }
    setUploadingId(exerciseId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const url = force
        ? `${API}/exercise/${exerciseId}/video?force=true`
        : `${API}/exercise/${exerciseId}/video`;
      const r = await axios.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Vídeo asociado a ${exerciseId} (${r.data.size_mb} MB · propagado a ${r.data.propagated_to_plans} planes)${force ? ' · OVERRIDE' : ''}`);
      await refreshExercises();
      // Auto-avanzar al siguiente sin vídeo
      const idx = exercises.findIndex((e) => e.exercise_id === exerciseId);
      const next = exercises.slice(idx + 1).find((e) => !e.video_url);
      if (next) setSelectedEx(next);
    } catch (e) {
      // Detección de DUPLICADO (HTTP 409)
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === 'duplicate_video') {
        const ok = window.confirm(
          `⚠ ARCHIVO DUPLICADO DETECTADO\n\n` +
          `Este video es BYTE A BYTE idéntico al ya asociado a:\n` +
          `   ${detail.existing_exercise_id} — ${detail.existing_name || '(sin nombre)'}\n\n` +
          `Probablemente seleccionaste el archivo equivocado en el explorador.\n\n` +
          `¿Querés subirlo IGUALMENTE a ${exerciseId}?\n` +
          `(Solo dale OK si estás 100% seguro de que el mismo video aplica a ambos ejercicios)`
        );
        setUploadingId(null);
        if (ok) {
          // Reintenta con force=true
          await uploadVideo(exerciseId, file, { force: true });
        }
        return;
      }
      toast.error(detail?.message || detail || 'Error subiendo vídeo');
    } finally {
      setUploadingId(null);
    }
  };

  const deleteVideo = async (exerciseId) => {
    if (!window.confirm(`¿Borrar el vídeo de ${exerciseId}?`)) return;
    try {
      await axios.delete(`${API}/exercise/${exerciseId}/video`);
      toast.success(`Vídeo de ${exerciseId} eliminado`);
      refreshExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error borrando vídeo');
    }
  };

  // ---------- BACKFILL MD5 hashes ----------
  const [backfillReport, setBackfillReport] = useState(null);
  const runBackfillMd5 = async () => {
    if (!window.confirm('Calcular MD5 de todos los videos ya subidos. Puede tardar 1-2 min según cuántos videos haya. ¿Continuar?')) return;
    setBusy('backfill', true);
    setBackfillReport(null);
    try {
      const r = await axios.post(`${API}/videos/backfill-md5`);
      setBackfillReport(r.data);
      const dups = r.data.duplicates_detected?.length || 0;
      if (dups > 0) {
        toast.warning(`Backfill OK · ${r.data.updated} hashes calculados · ${dups} duplicados encontrados ⚠`);
      } else {
        toast.success(`Backfill OK · ${r.data.updated} hashes calculados · sin duplicados ✓`);
      }
      await refreshExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error backfill');
    } finally {
      setBusy('backfill', false);
    }
  };

  // ---------- 6. CONECTAR PLAN SENIOR ----------
  const [connectReport, setConnectReport] = useState(null);
  const runConnectSenior = async (dryRun = true) => {
    setBusy('connect', true);
    setConnectReport(null);
    try {
      const r = await axios.post(`${API}/connect-senior-plan?dry_run=${dryRun}`);
      setConnectReport(r.data);
      if (dryRun) {
        toast.info(`Simulación: ${r.data.connected}/${r.data.total_exercises_in_plan} conectables`);
      } else {
        toast.success(`Plan Senior conectado: ${r.data.connected} ejercicios actualizados`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error conectando plan Senior');
    } finally {
      setBusy('connect', false);
    }
  };

  // ---------- 1. BACKUP ----------
  const runBackup = async () => {
    setBusy('backup', true);
    try {
      const r = await axios.post(`${API}/backup/full`);
      toast.success(`Backup creado: ${r.data.filename} (${r.data.size_kb} KB)`);
      refreshBackups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en backup');
    } finally {
      setBusy('backup', false);
    }
  };

  // ---------- 2. WIPE PREVIEW ----------
  const runPreview = async () => {
    setBusy('preview', true);
    try {
      const r = await axios.post(`${API}/wipe/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy('preview', false);
    }
  };

  // ---------- 2b. WIPE EXECUTE ----------
  const runWipe = async () => {
    if (confirmText !== 'BORRAR LIBRERIA') {
      toast.error('Escribe exactamente: BORRAR LIBRERIA');
      return;
    }
    if (!pin || pin.length < 4) {
      toast.error('Ingresa el PIN de admin');
      return;
    }
    if (!window.confirm('¿Confirmas el wipe definitivo? Los planes mantendrán nombres pero quedarán sin exercise_id hasta re-asociar.')) return;
    setBusy('wipe', true);
    try {
      const r = await axios.post(`${API}/wipe/execute`, {
        confirm_pin: pin,
        wipe_videos_in_r2: wipeR2,
      });
      toast.success(`Wipe completado: ${r.data.exercises_deleted} ejercicios borrados`);
      setPin('');
      setConfirmText('');
      refreshStats();
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en wipe');
    } finally {
      setBusy('wipe', false);
    }
  };

  // ---------- 3. IMPORT ----------
  const downloadTemplate = async () => {
    window.open(`${API}/import/template.csv`, '_blank');
  };

  const runImport = async () => {
    if (!csvFile) {
      toast.error('Selecciona un CSV primero');
      return;
    }
    setBusy('import', true);
    const fd = new FormData();
    fd.append('file', csvFile);
    try {
      const r = await axios.post(`${API}/import/csv?replace_all=${replaceAll}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Importado: ${r.data.inserted} nuevos, ${r.data.skipped_existing} ya existían`);
      if (r.data.errors?.length > 0) {
        toast.warning(`${r.data.errors.length} filas con errores. Revisa la consola.`);
        console.warn('Errores en CSV:', r.data.errors);
      }
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refreshStats();
      refreshExercises();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en import');
    } finally {
      setBusy('import', false);
    }
  };

  // ---------- 4. RELINK ORPHANS ----------
  const loadOrphans = async () => {
    setBusy('orphans', true);
    try {
      const r = await axios.get(`${API}/plans/orphans`);
      setOrphans(r.data.orphans || []);
      toast.info(`${r.data.total} ejercicios huérfanos en planes`);
    } catch (e) {
      toast.error('Error cargando huérfanos');
    } finally {
      setBusy('orphans', false);
    }
  };

  const relinkOne = async (orphan, exerciseId) => {
    if (!exerciseId) return;
    try {
      await axios.post(`${API}/plans/relink`, {
        plan_id: orphan.plan_id,
        exercise_index: orphan.exercise_index,
        exercise_id: exerciseId.toUpperCase(),
        context: orphan.context || 'flat',
      });
      toast.success(`Asociado a ${exerciseId}`);
      loadOrphans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asociar');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="library-reset-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-emerald-400" />
              Reset Librería · Arquitectura Limpia
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Regla única: <code className="px-1.5 py-0.5 bg-zinc-800 rounded text-emerald-300">1 ejercicio = 1 exercise_id (GF-XXX) = 1 vídeo (GF-XXX.mp4)</code>
            </p>
          </div>
          {stats && (
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline" className="border-zinc-700"><Database className="h-3 w-3 mr-1" /> Total: {stats.total}</Badge>
              <Badge variant="outline" className="border-emerald-700/40 text-emerald-400">v2: {stats.schema_v2}</Badge>
              <Badge variant="outline" className="border-blue-700/40 text-blue-400">Con vídeo: {stats.with_video}</Badge>
              <Badge variant="outline" className="border-amber-700/40 text-amber-400">Activos: {stats.active}</Badge>
            </div>
          )}
        </div>

        {/* PASO 1: BACKUP */}
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30" data-testid="step-backup">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-5 w-5 text-emerald-400" />
              Paso 1 · Backup completo (obligatorio)
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Exporta TODOS los datos reales (usuarios, planes, sesiones, logs, RPE, cargas, métricas) a un JSON con fecha. Sin backup reciente (&lt; 24h), el wipe está bloqueado.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={runBackup} disabled={loading.backup} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-backup">
              {loading.backup ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando...</> : <><Database className="h-4 w-4 mr-2" /> Crear backup ahora</>}
            </Button>
            {backups.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Backups recientes:</p>
                {backups.slice(0, 3).map((b) => (
                  <div key={b.filename} className="flex items-center justify-between bg-zinc-900/60 rounded-md px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      <div className="truncate">
                        <div className="text-foreground truncate">{b.filename}</div>
                        <div className="text-xs text-muted-foreground">{b.size_kb} KB · {new Date(b.modified_at).toLocaleString('es-ES')}</div>
                      </div>
                    </div>
                    <a href={`${API}/backup/download/${b.filename}`}
                       target="_blank" rel="noreferrer"
                       className="text-emerald-400 hover:text-emerald-300 flex-shrink-0 ml-2">
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* PASO 2: WIPE */}
        <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/30" data-testid="step-wipe">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Paso 2 · Wipe librería antigua
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Borra <strong>exercises</strong> y <strong>trainer_exercises</strong>. Los planes mantienen <code className="px-1 bg-zinc-800 rounded">exercise_name</code> como snapshot, pero se limpia <code className="px-1 bg-zinc-800 rounded">exercise_id</code> y <code className="px-1 bg-zinc-800 rounded">video_url</code>. Logs de usuarios <strong>se preservan</strong>.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={runPreview} disabled={loading.preview} variant="outline" data-testid="btn-wipe-preview">
              {loading.preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-2" />}
              Ver preview
            </Button>
            {preview && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                <div className="p-2 bg-zinc-900 rounded-md">
                  <div className="text-xl font-bold text-red-400">{preview.exercises_to_delete}</div>
                  <div className="text-xs text-muted-foreground">Ejercicios borrar</div>
                </div>
                <div className="p-2 bg-zinc-900 rounded-md">
                  <div className="text-xl font-bold text-red-400">{preview.trainer_exercises_to_delete}</div>
                  <div className="text-xs text-muted-foreground">Trainer ej.</div>
                </div>
                <div className="p-2 bg-zinc-900 rounded-md">
                  <div className="text-xl font-bold text-amber-400">{preview.plans_total}</div>
                  <div className="text-xs text-muted-foreground">Planes (preserve)</div>
                </div>
                <div className="p-2 bg-zinc-900 rounded-md">
                  <div className="text-xl font-bold text-emerald-400">{preview.user_logs_to_preserve}</div>
                  <div className="text-xs text-muted-foreground">Logs preserva</div>
                </div>
              </div>
            )}
            <div className="border-t border-zinc-800 pt-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                Acción irreversible. Asegúrate de tener backup &lt; 24h.
              </div>
              <Input
                type="text"
                placeholder='Escribe: BORRAR LIBRERIA'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                data-testid="input-confirm-text"
                className="bg-zinc-900"
              />
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="PIN admin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  data-testid="input-pin"
                  className="bg-zinc-900 max-w-[160px]"
                />
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input type="checkbox" checked={wipeR2} onChange={(e) => setWipeR2(e.target.checked)} data-testid="checkbox-wipe-r2" />
                  Borrar también vídeos en R2
                </label>
              </div>
              <Button
                onClick={runWipe}
                disabled={loading.wipe || confirmText !== 'BORRAR LIBRERIA' || !pin}
                className="bg-red-600 hover:bg-red-700 w-full"
                data-testid="btn-wipe-execute"
              >
                {loading.wipe ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Borrando...</> : <><Lock className="h-4 w-4 mr-2" /> Ejecutar wipe definitivo</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* PASO 3: IMPORT */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/30" data-testid="step-import">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-400" />
              Paso 3 · Importar nueva librería oficial (CSV)
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Columnas requeridas: <code className="px-1 bg-zinc-800 rounded">exercise_id, nombre_oficial, categoria, patron, nivel, estado, video_filename</code>. exercise_id debe ser GF-XXX. Listas cerradas en categoria/nivel/estado.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={downloadTemplate} variant="outline" size="sm" data-testid="btn-template">
              <Download className="h-4 w-4 mr-2" /> Descargar plantilla CSV
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0])}
              data-testid="input-csv"
              className="bg-zinc-900"
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={replaceAll} onChange={(e) => setReplaceAll(e.target.checked)} data-testid="checkbox-replace" />
              Reemplazar TODA la librería (vacía antes de importar)
            </label>
            <Button onClick={runImport} disabled={loading.import || !csvFile} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-import">
              {loading.import ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importando...</> : <><Upload className="h-4 w-4 mr-2" /> Importar CSV</>}
            </Button>
          </CardContent>
        </Card>

        {/* PASO 4: RELINK ORPHANS */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/30" data-testid="step-relink">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-5 w-5 text-purple-400" />
              Paso 4 · Re-asociar ejercicios huérfanos en planes
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Lista los ejercicios dentro de planes existentes que NO tienen un <code className="px-1 bg-zinc-800 rounded">exercise_id</code> válido. Asóciales el ID oficial nuevo (GF-XXX).
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={loadOrphans} disabled={loading.orphans} variant="outline" data-testid="btn-load-orphans">
              {loading.orphans ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-2" />}
              Cargar huérfanos
            </Button>
            {orphans.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {orphans.map((o, i) => (
                  <OrphanRow key={`${o.plan_id}-${o.exercise_index}-${i}`} orphan={o} onRelink={relinkOne} />
                ))}
              </div>
            )}
            {orphans.length === 0 && stats && (
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Todos los planes están conectados a la librería oficial.
              </p>
            )}
          </CardContent>
        </Card>

        {/* PASO 5: ASOCIAR VÍDEOS POR ID */}        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/30" data-testid="step-videos">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Video className="h-5 w-5 text-amber-400" />
              Paso 5 · Asociar vídeos por exercise_id
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Sube el vídeo de cada ejercicio. El archivo se debe llamar igual que el ID (ej: <code className="px-1 bg-zinc-800 rounded">GF-001.mp4</code>). Preview lateral inmediato + propagación automática a todos los planes.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={runBackfillMd5}
                disabled={loading.backfill}
                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="btn-backfill-md5"
              >
                {loading.backfill ? (
                  <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Calculando hashes...</>
                ) : (
                  <>🛡️ Auditar duplicados (calcular MD5 de videos viejos)</>
                )}
              </Button>
              {backfillReport && (
                <div className="text-xs text-muted-foreground">
                  Hashes calculados: <span className="text-foreground font-semibold">{backfillReport.updated}</span>
                  {' · '}Únicos: <span className="text-foreground font-semibold">{backfillReport.total_unique_videos}</span>
                  {backfillReport.duplicates_detected?.length > 0 && (
                    <span className="ml-2 text-red-400 font-semibold" data-testid="duplicates-warning">
                      ⚠ {backfillReport.duplicates_detected.length} duplicados detectados
                    </span>
                  )}
                </div>
              )}
            </div>
            {backfillReport?.duplicates_detected?.length > 0 && (
              <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded p-2 text-xs">
                <p className="font-semibold text-red-300 mb-1">Estos ejercicios tienen el MISMO video que otro:</p>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                  {backfillReport.duplicates_detected.map((d) => (
                    <li key={d.exercise_id} className="text-red-200/90">
                      • <code>{d.exercise_id}</code> es idéntico a <code>{d.duplicate_of}</code> — corregí uno de los dos
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {exercises.length === 0 ? (
              <p className="text-sm text-muted-foreground">Importa primero la librería (Paso 3) para asociar vídeos.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Lista de ejercicios */}
                <div className="lg:col-span-2 space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  <div className="relative sticky top-0 bg-zinc-950 pb-2 z-10">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar GF-XXX o nombre..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 bg-zinc-900 h-9 text-sm"
                      data-testid="search-exercises"
                    />
                  </div>
                  {exercises
                    .filter((e) => {
                      const t = searchTerm.toLowerCase();
                      return !t || e.exercise_id.toLowerCase().includes(t) || (e.nombre_oficial || '').toLowerCase().includes(t);
                    })
                    .map((ex) => (
                      <ExerciseRow
                        key={ex.exercise_id}
                        ex={ex}
                        selected={selectedEx?.exercise_id === ex.exercise_id}
                        uploading={uploadingId === ex.exercise_id}
                        onSelect={() => setSelectedEx(ex)}
                        onUpload={(file) => uploadVideo(ex.exercise_id, file)}
                        onDelete={() => deleteVideo(ex.exercise_id)}
                      />
                    ))}
                </div>

                {/* Preview lateral + ficha */}
                <div className="lg:sticky lg:top-4 lg:self-start">
                  {selectedEx ? (
                    <PreviewPanel ex={selectedEx} />
                  ) : (
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-6 text-center text-sm text-muted-foreground">
                      Selecciona un ejercicio para ver su preview
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PASO 6: CONECTAR PLAN SENIOR */}
        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border-cyan-500/30" data-testid="step-connect-senior">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-5 w-5 text-cyan-400" />
              Paso 6 · Conectar plan Senior a la librería oficial
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Aplica el mapeo aprobado: cada ejercicio del plan Senior se vincula a su <code className="px-1 bg-zinc-800 rounded">exercise_id</code> oficial.
              <strong className="text-cyan-300"> Primero simula</strong>, luego ejecuta de verdad.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => runConnectSenior(true)}
                disabled={loading.connect}
                variant="outline"
                className="border-cyan-500/40"
                data-testid="btn-connect-dry-run"
              >
                {loading.connect ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                Simular (dry-run)
              </Button>
              <Button
                onClick={() => {
                  if (!window.confirm('¿Aplicar el mapeo definitivo al plan Senior? Esto modifica el plan en la base de datos.')) return;
                  runConnectSenior(false);
                }}
                disabled={loading.connect || !connectReport}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="btn-connect-apply"
              >
                <Link2 className="h-4 w-4 mr-2" /> Aplicar definitivo
              </Button>
            </div>

            {connectReport && (
              <div className="bg-zinc-900/60 rounded-md p-3 space-y-2 text-sm">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <Stat label="Total" value={connectReport.total_exercises_in_plan} />
                  <Stat label="Conectados" value={connectReport.connected} color="emerald" />
                  <Stat label="Huérfanos" value={connectReport.orphans_count} color={connectReport.orphans_count > 0 ? 'red' : 'zinc'} />
                  <Stat label="Pendientes" value={connectReport.pendientes_revision?.length || 0} color="amber" />
                </div>
                <div className="text-xs text-muted-foreground">
                  Plan: <strong className="text-foreground">{connectReport.plan_name}</strong> · modo: {connectReport.dry_run ? 'SIMULACIÓN (no se ha escrito nada)' : 'EJECUTADO'}
                </div>
                {connectReport.pendientes_revision?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-2 text-xs text-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                    <strong>{connectReport.pendientes_revision.length} entrada(s) marcadas como pendientes de revisión técnica</strong>
                    {' '}({connectReport.pendientes_revision[0]?.new_exercise_id})
                  </div>
                )}
                {connectReport.orphans?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-md p-2 text-xs">
                    <strong className="text-red-300">Huérfanos sin mapeo:</strong>
                    <ul className="mt-1 space-y-0.5">
                      {connectReport.orphans.slice(0, 10).map((o, i) => (
                        <li key={i}>• S{o.week}.{o.session_index}.{o.exercise_index}: "{o.name}"</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
};

const Stat = ({ label, value, color = 'zinc' }) => {
  const colors = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
    zinc: 'text-zinc-300',
  };
  return (
    <div className="p-2 bg-zinc-950 rounded">
      <div className={`text-xl font-bold ${colors[color]}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
};

const OrphanRow = ({ orphan, onRelink }) => {
  const [val, setVal] = useState('');
  return (
    <div className="flex items-center gap-2 bg-zinc-900/60 rounded-md px-3 py-2 text-sm" data-testid="orphan-row">
      <div className="flex-1 min-w-0">
        <div className="text-foreground truncate">{orphan.exercise_name || '(sin nombre)'}</div>
        <div className="text-xs text-muted-foreground truncate">
          {orphan.plan_name} · <span className="text-zinc-500">{orphan.context}</span> · pos {orphan.exercise_index}
        </div>
      </div>
      <Input
        type="text"
        placeholder="GF-XXX"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="w-28 bg-zinc-950 text-xs h-8"
        data-testid="orphan-input"
      />
      <Button
        size="sm"
        onClick={() => onRelink(orphan, val)}
        disabled={!val}
        className="h-8 bg-purple-600 hover:bg-purple-700"
        data-testid="orphan-link-btn"
      >
        <Link2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

const ExerciseRow = ({ ex, selected, uploading, onSelect, onUpload, onDelete }) => {
  const inputRef = useRef(null);
  const hasVideo = !!ex.video_url;
  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${
        selected ? 'bg-amber-500/15 border border-amber-500/40' : 'bg-zinc-900/60 hover:bg-zinc-900 border border-transparent'
      }`}
      data-testid={`ex-row-${ex.exercise_id}`}
    >
      <Badge variant="outline" className={`font-mono text-xs ${hasVideo ? 'border-emerald-700/40 text-emerald-400' : 'border-zinc-700'}`}>
        {ex.exercise_id}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="text-foreground truncate text-sm">{ex.nombre_oficial || ex.name}</div>
        <div className="text-xs text-muted-foreground truncate">{ex.grupo_muscular || '—'} · {ex.patron || '—'}</div>
      </div>
      {hasVideo ? (
        <>
          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`del-video-${ex.exercise_id}`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid={`file-input-${ex.exercise_id}`}
          />
          <Button
            size="sm"
            disabled={uploading}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            className="h-7 bg-amber-600 hover:bg-amber-700 text-xs"
            data-testid={`upload-video-${ex.exercise_id}`}
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" /> Vídeo</>}
          </Button>
        </>
      )}
    </div>
  );
};

const PreviewPanel = ({ ex }) => {
  const videoSrc = ex.video_url
    ? (ex.video_url.startsWith('http') ? ex.video_url : `${process.env.REACT_APP_BACKEND_URL}${ex.video_url}`)
    : null;

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden" data-testid="preview-panel">
      <div className="aspect-video bg-black flex items-center justify-center relative">
        {videoSrc ? (
          <video
            key={videoSrc}
            src={videoSrc}
            controls
            preload="metadata"
            className="w-full h-full object-contain"
            data-testid="preview-video"
          />
        ) : (
          <div className="text-center text-muted-foreground p-4">
            <PlayCircle className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin vídeo asociado todavía</p>
            <p className="text-xs mt-1">Sube <code className="px-1 bg-zinc-800 rounded font-mono">{ex.exercise_id}.mp4</code></p>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="font-mono border-amber-700/40 text-amber-400">{ex.exercise_id}</Badge>
          {ex.activo
            ? <Badge variant="outline" className="border-emerald-700/40 text-emerald-400">Activo</Badge>
            : <Badge variant="outline" className="border-zinc-700">Inactivo</Badge>}
          {ex.video_size_mb && <Badge variant="outline" className="border-zinc-700 text-xs">{ex.video_size_mb} MB</Badge>}
        </div>
        <h3 className="text-base font-semibold text-foreground leading-tight">{ex.nombre_oficial || ex.name}</h3>
        {ex.descripcion && (
          <p className="text-xs text-muted-foreground leading-relaxed">{ex.descripcion}</p>
        )}
        <div className="grid grid-cols-2 gap-2 pt-2 text-xs">
          <div>
            <div className="text-muted-foreground">Grupo</div>
            <div className="text-foreground">{ex.grupo_muscular || '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Patrón</div>
            <div className="text-foreground">{ex.patron || '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryResetPage;
